<template>
  <Teleport to="body">
    <div class="fixed top-4 right-4 z-50 flex flex-col gap-3 max-w-md w-full pointer-events-none pt-safe">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="[
            'glass-surface pointer-events-auto bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 border-l-4',
            'transition-all duration-300 transform',
            getToastColor(toast.level || toast.type),
          ]">
          <div class="flex items-start gap-3">
            <!-- Icon -->
            <div :class="['p-2 rounded-lg flex-shrink-0', getIconBg(toast.level || toast.type)]">
              <span class="material-symbols-outlined text-white text-lg">
                {{ getIcon(toast.level || toast.type) }}
              </span>
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                {{ toast.title }}
              </h4>
              <p class="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                {{ toast.message }}
              </p>
              <!-- TTS indicator -->
              <div v-if="toast.ttsMessage" class="flex items-center gap-1 mt-1">
                <span class="material-symbols-outlined text-xs text-gray-400">volume_up</span>
                <span class="text-[10px] text-gray-400 italic">{{ toast.ttsMessage }}</span>
              </div>
            </div>

            <!-- Close button -->
            <button
              @click="removeToast(toast.id)"
              class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
              <span class="material-symbols-outlined text-lg">close</span>
            </button>
          </div>

          <!-- Camera preview (secondary stream) -->
          <div v-if="toast.cameraId" class="mt-3 rounded-lg overflow-hidden bg-black" style="aspect-ratio: 16/9">
            <WebRTCPlayer
              :camera-id="toast.cameraId"
              :poster-src="getPosterSnapshot(toast.cameraId)"
              :auto-connect="true"
              :muted="true"
              :controls="false"
              :show-status="true"
              :show-retry="false"
              stream-preference="prefer-sub"
              class="w-full h-full" />
          </div>
          <!-- Progress bar for auto-dismiss (non-critical) -->
          <div
            v-if="toast.level !== 'critical' && toast.autoDismissMs"
            class="mt-2 h-0.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              class="h-full bg-current rounded-full toast-progress"
              :style="{ animationDuration: toast.autoDismissMs + 'ms' }"
              :class="getProgressColor(toast.level || toast.type)" />
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useNotificationStore, type Notification } from '../stores/notifications';
import { useCameraSnapshotsStore } from '../stores/cameraSnapshots';
import { useSocketStore } from '../stores/socket';
import { isCapacitor } from '../config/runtime';
import { localeTag } from '../utils/formatDate';
import { playSound } from '../utils/alarmSounds';
import { playHubVoiceClip, speakInHubVoice } from '../services/hubVoice';
import WebRTCPlayer from './WebRTCPlayer.vue';

interface Toast {
  id: string;
  title: string;
  message: string;
  type: string;
  /** Notification level: critical | warning | info */
  level?: string;
  /** TTS message to speak aloud */
  ttsMessage?: string;
  /** Auto-dismiss duration in ms (0 = sticky) */
  autoDismissMs?: number;
  /** Camera id — when set, a live camera preview is shown (prefer-sub/secondary stream) */
  cameraId?: string;
}

const notificationStore = useNotificationStore();
const cameraSnapshotsStore = useCameraSnapshotsStore();
const socketStore = useSocketStore();
const toasts = ref<Toast[]>([]);
/** Whether TTS is enabled (user can toggle) */
const ttsEnabled = ref(true);

// ----- TTS helpers -----

/**
 * Speak a notice (card `t_92849b4b`). The hub is the voice authority: its
 * Piper addon holds the voice configured for this hub (`Settings → Agent`,
 * `POST /api/agent/config`), and its `/wyoming` namespace renders arbitrary
 * text with it — so the sentence is spoken in the same voice as every other
 * announcement on this hub. `services/hubVoice.ts` holds the exchange and its
 * tests.
 *
 * The OS voices are only the fallback, for a surface with no hub socket
 * (browser / cloud / before the shell connects) or a hub that cannot
 * synthesize. Electron's OS voices are six English ones, so on a Slovene panel
 * they read the sentence in the language it was not written in — the "different
 * voice" the owner heard.
 */
async function speak(text: string, level?: string): Promise<void> {
  if (!ttsEnabled.value) return;

  const spoken = await speakInHubVoice(socketStore.socket, text, {
    play: (clip) => playHubVoiceClip(clip, level === 'critical' ? 'urgent' : 'normal'),
  });
  if (spoken) return;

  speakWithOperatingSystemVoice(text, level);
}

/**
 * Fallback: the Web Speech Synthesis API with the OS voices.
 *
 * The voice follows the APP's language, not a literal: `tts_message` is already
 * translated copy, and a Slovene panel announcing it in an `en-US` voice reads
 * the sentence it just spoke in the wrong language. `localeTag()` is the app's
 * single answer to "which BCP-47 tag is the app using right now" (see its
 * header) — `sl-SI`, `de-DE`, … and, for an English UI, the runtime's own
 * English region. The Web Speech API picks the closest installed voice for the
 * tag, so a device with no voice for it falls back instead of going silent.
 */
function speakWithOperatingSystemVoice(text: string, level?: string) {
  if (!('speechSynthesis' in window)) {
    console.warn('[NotificationToast] SpeechSynthesis API not available');
    return;
  }

  // Cancel any queued utterances to avoid pile-up
  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = localeTag();
  utterance.rate = 1.0;
  utterance.pitch = level === 'critical' ? 1.2 : 1.0;
  utterance.volume = level === 'critical' ? 1.0 : 0.8;

  window.speechSynthesis.speak(utterance);
}

/** Play a short alert tone via AudioContext for critical/warning notifications */
// Singleton AudioContext — creating a new one on every notification causes
// audio hardware errors and a GPU compositor crash on Android WebView.
let _audioCtx: AudioContext | null = null;
function getAudioCtx(): AudioContext | null {
  // Skip on Capacitor (Android/iOS) — WebView AudioContext is unreliable
  if (isCapacitor()) return null;
  try {
    if (!_audioCtx || _audioCtx.state === 'closed') {
      _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return _audioCtx;
  } catch {
    return null;
  }
}

function playAlertTone(level: string) {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;

    // Resume if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') ctx.resume();

    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    if (level === 'critical') {
      // Urgent double-beep
      oscillator.frequency.value = 880;
      gain.gain.value = 0.3;
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.15);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = 880;
      gain2.gain.value = 0.3;
      osc2.start(ctx.currentTime + 0.2);
      osc2.stop(ctx.currentTime + 0.35);
    } else if (level === 'warning') {
      // Single soft beep
      oscillator.frequency.value = 660;
      gain.gain.value = 0.2;
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.12);
    }
  } catch {
    // AudioContext may not be available in all contexts
  }
}

// ----- Auto-dismiss durations per level -----

function getAutoDismissMs(level?: string, type?: string): number {
  if (level === 'critical') return 0; // Sticky — manual dismiss only
  if (level === 'warning' || type === 'device_alert') return 10000;
  if (type === 'error') return 10000;
  return 5000;
}

// ----- Watch for newly pushed notifications -----

/**
 * Announce the notices the NOTICE LAYER handed to this surface. The store only
 * raises `announceSeq` on the surface the operator is looking at (card
 * t_4677fac1): the other surfaces of the same hub hold the notice and badge,
 * which is why this watches `announceSeq` and not `liveSeq` — watching every
 * live push is what painted the balloon twice and spoke it twice.
 *
 * Driven by a sequence instead of the list length, so REST fetches never
 * trigger a balloon and the first push after a page load is announced too
 * (a `lastNotificationCount > 0` guard used to swallow it).
 */
watch(
  () => notificationStore.announceSeq,
  () => {
    const n: Notification | null = notificationStore.announcement;
    if (!n) return;

    const level = n.level;
    const autoDismissMs = getAutoDismissMs(level, n.notification_type);

    addToast({
      id: n.notification_id,
      title: n.title,
      message: n.message,
      type: n.notification_type,
      level,
      ttsMessage: n.tts_message,
      autoDismissMs,
      cameraId: n.camera_id,
    });

    // Play alert tone for warning / critical
    if (level === 'critical' || level === 'warning') {
      playAlertTone(level);
    }

    // Play sound preset if specified
    if (n.sound && n.sound !== 'none') {
      playSound(n.sound).catch(() => {});
    }

    // Speak TTS message — once per event, because only one surface gets here
    if (n.tts_message) {
      void speak(n.tts_message, level);
    }
  },
);

onMounted(() => {
  // Bootstrap notification pipeline immediately — independent of drawer state
  notificationStore.initialize();
  // Self-heal: make sure the CURRENT socket carries the listener (a hub switch
  // made before this component mounted used to leave notifications dead).
  notificationStore.ensureSocketListener();

  // Also re-initialize once socket connects if it isn't ready yet
  if (!socketStore.connected) {
    const unwatch = watch(
      () => socketStore.connected,
      (connected) => {
        if (connected) {
          notificationStore.initialize();
          notificationStore.ensureSocketListener();
          unwatch();
        }
      },
    );
  }
});

// ----- Toast management -----

function addToast(toast: Toast) {
  // Prevent duplicates
  if (toasts.value.find((t) => t.id === toast.id)) return;

  if (toast.cameraId) {
    void cameraSnapshotsStore.fetchSnapshot(toast.cameraId);
  }

  toasts.value.push(toast);

  // Auto-dismiss (0 = sticky for critical)
  if (toast.autoDismissMs && toast.autoDismissMs > 0) {
    setTimeout(() => {
      removeToast(toast.id);
    }, toast.autoDismissMs);
  }
}

function removeToast(id: string) {
  const index = toasts.value.findIndex((t) => t.id === id);
  if (index !== -1) {
    toasts.value.splice(index, 1);
  }
}

function getPosterSnapshot(cameraId?: string): string | null {
  return cameraId ? cameraSnapshotsStore.getSnapshotUrl(cameraId) : null;
}

// ----- Visual helpers -----

function getIcon(levelOrType: string): string {
  const icons: Record<string, string> = {
    critical: 'crisis_alert',
    warning: 'warning',
    info: 'info',
    device_alert: 'warning',
    automation: 'play_circle',
    routine: 'schedule',
    system: 'settings',
    success: 'done',
    error: 'error',
  };
  return icons[levelOrType] || 'notifications';
}

function getIconBg(levelOrType: string): string {
  const classes: Record<string, string> = {
    critical: 'bg-red-600',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
    device_alert: 'bg-red-500',
    automation: 'bg-blue-500',
    routine: 'bg-indigo-500',
    system: 'bg-purple-500',
    success: 'bg-green-500',
    error: 'bg-red-500',
  };
  return classes[levelOrType] || 'bg-gray-500';
}

function getToastColor(levelOrType: string): string {
  const classes: Record<string, string> = {
    critical: 'border-red-600',
    warning: 'border-amber-500',
    info: 'border-blue-500',
    device_alert: 'border-red-500',
    automation: 'border-blue-500',
    routine: 'border-indigo-500',
    system: 'border-purple-500',
    success: 'border-green-500',
    error: 'border-red-500',
  };
  return classes[levelOrType] || 'border-gray-500';
}

function getProgressColor(levelOrType: string): string {
  const classes: Record<string, string> = {
    critical: 'bg-red-600',
    warning: 'bg-amber-500',
    info: 'bg-blue-400',
    device_alert: 'bg-red-400',
    error: 'bg-red-400',
  };
  return classes[levelOrType] || 'bg-blue-400';
}
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Auto-dismiss progress bar animation */
@keyframes toast-countdown {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

.toast-progress {
  animation: toast-countdown linear forwards;
}
</style>
